import React, { useEffect, useRef, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import PhotoButton from "../../ui/PhotoButton.jsx";
import { phoneConfirm } from "../../ui/dialogs.jsx";
import { phoneUi } from "../../ui/phoneUi";
import { defaultKindFor, isManifestDoc, loadOrderDocuments, saveDeliveryRecord } from "../../../features/salesOrders/pod";
import "../../mobile.css";

// Proof of delivery / pickup from the phone's camera (plan Phase 2): who received it, which pallets,
// the photos (the camera's originals: the API reads their time and GPS), then the PIN. The requests
// are features/salesOrders/pod.js, the desktop photo panel's own. Hiding a record, the packet and
// emailing it stay on the desktop.

const fmtBytes = (n) => (n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

export default function PodSheet({ open, plan, soId, pallets, limits, onClose, onSaved }) {
  const [kind, setKind] = useState(defaultKindFor(plan?.fulfillment_type));
  const [palletIds, setPalletIds] = useState([]);
  const [receivedBy, setReceivedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [manifestDocId, setManifestDocId] = useState("");
  const [files, setFiles] = useState([]); // {file, status, progress, error}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedEvent, setSavedEvent] = useState(null);
  const maxFiles = limits?.max_photos_per_event || 30;
  const maxBytes = limits?.max_file_bytes || 25 * 1024 * 1024;

  // Start clean each time the sheet OPENS, never on a re-render while it is open (the screen re-reads
  // the delivery records under it): a reset there would drop the photos, and after a failed photo
  // the saved record, so a retry would make a second one.
  const wasOpen = useRef(false);
  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return undefined;
    setKind(defaultKindFor(plan?.fulfillment_type));
    setPalletIds((pallets || []).map((p) => p.pallet_id));
    setReceivedBy("");
    setNotes("");
    setFiles([]);
    setError(null);
    setSavedEvent(null);
    let live = true;
    loadOrderDocuments(soId).then((docs) => {
      if (!live) return;
      const m = (docs || []).find(isManifestDoc);
      setManifestDocId(m ? m.document_id : "");
    }).catch(() => {});
    return () => { live = false; };
    // Only `open` restarts it; plan / pallets are read as they are when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addPhotos = (picked) => {
    setFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (next.length >= maxFiles) break;
        next.push({ file: f, status: f.size > maxBytes ? "rejected" : "queued", progress: 0, error: f.size > maxBytes ? `${fmtBytes(f.size)} is over the ${fmtBytes(maxBytes)} limit` : "" });
      }
      return next;
    });
  };
  const togglePallet = (id) => setPalletIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    setError(null);
    const queued = files.map((f, i) => ({ ...f, i })).filter((f) => f.status === "queued" || f.status === "failed");
    if (!savedEvent && palletIds.length === 0) { setError("Pick at least one pallet."); return; }
    if (!savedEvent && queued.length === 0 && !(await phoneConfirm("No photos selected. Create the record without photos?", { title: "No photos?", confirmLabel: "Record it" }))) return;
    if (savedEvent && queued.length === 0) { onSaved(); return; }
    const pin = await phoneUi.pin("Enter your PIN to record the delivery:");
    if (!pin) return;
    setSaving(true);
    const outcome = {};
    try {
      const r = await saveDeliveryRecord({
        planId: plan.plan_id,
        // After a photo failed, the record exists: add the remaining photos to it.
        mode: savedEvent ? "add" : "new",
        event: savedEvent,
        pin,
        fields: { kind, palletIds, occurredAt: "", receivedBy, notes, manifestDocId },
        uploads: queued.map((f) => ({ file: f.file, kind: "photo" })),
        onFile: (k, patch) => {
          if (patch.status) outcome[k] = patch.status;
          setFiles((prev) => prev.map((x, j) => (j === queued[k].i ? { ...x, ...patch } : x)));
        },
      });
      const failed = Object.values(outcome).filter((st) => st === "failed").length;
      if (failed) {
        setSavedEvent({ event_id: r.eventId });
        setError(`${failed} photo(s) didn't upload. The record is saved: try them again, or remove them and tap Done.`);
      } else {
        onSaved();
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <button type="button" className="btn btn-primary btn-lg w-100" disabled={saving} onClick={submit} data-testid="pod-submit">
      {saving ? <><span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Uploading…</> : savedEvent ? "Done" : "Record delivery"}
    </button>
  );

  return (
    <FullScreenPanel open={open} title={kind === "pickup" ? "Proof of pickup" : "Proof of delivery"} onClose={saving ? undefined : onClose} footer={footer} testId="so-pod-sheet">
      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}
      {!savedEvent && (
        <>
          <label className="form-label" htmlFor="pod-kind">Kind</label>
          <select id="pod-kind" className="form-select mb-3" value={kind} onChange={(e) => setKind(e.target.value)} disabled={saving}>
            <option value="delivery">Delivery (we delivered)</option>
            <option value="pickup">Customer pickup</option>
            <option value="carrier_handoff">Carrier handoff</option>
          </select>
          <label className="form-label" htmlFor="pod-received">Received by</label>
          <input id="pod-received" className="form-control mb-3" placeholder="name on the manifest" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} disabled={saving} />
          <div className="form-label">Pallets</div>
          <div className="mb-3">
            {(pallets || []).map((p) => (
              <label key={p.pallet_id} className="form-check">
                <input type="checkbox" className="form-check-input" checked={palletIds.includes(p.pallet_id)} onChange={() => togglePallet(p.pallet_id)} disabled={saving} />
                <span className="form-check-label">{p.short_id || `P${p.pallet_num}`} <span className="jq-muted">({p.status})</span></span>
              </label>
            ))}
          </div>
          <label className="form-label" htmlFor="pod-notes">Notes</label>
          <input id="pod-notes" className="form-control mb-3" placeholder="e.g. left at dock 3" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} />
          {manifestDocId ? <div className="jq-muted mb-2">The signed manifest on this order will be linked.</div> : null}
        </>
      )}
      {/* No `capture`: iPhone then offers the camera AND the photo library (photos shot in the
          Camera app keep their time and GPS; a camera opened from the page may have no location). */}
      <PhotoButton multiple capture={null} onPick={addPhotos} disabled={saving || files.length >= maxFiles} label="Add photos" testId="pod-photo" />
      <div className="jq-muted mt-1 mb-2">Take them now or pick them from your photos. The originals are uploaded as they are (time, and location when the phone recorded it). Up to {maxFiles}.</div>
      {files.map((f, i) => (
        <div key={i} className="jq-task-line" data-testid="pod-file">
          <span className="jq-task-line-main">
            {f.file.name} <span className="jq-muted">{fmtBytes(f.file.size)}</span>
            {f.status === "uploading" && <div className="jq-muted">Uploading {f.progress}%</div>}
            {f.status === "processing" && <div className="jq-muted">Reading the photo…</div>}
            {f.status === "done" && <div className="text-success">Saved</div>}
            {(f.status === "failed" || f.status === "rejected") && <div className="text-danger">{f.error}</div>}
          </span>
          {(f.status === "queued" || f.status === "failed" || f.status === "rejected") && !saving && (
            <button type="button" className="btn btn-link text-danger" aria-label={`Remove ${f.file.name}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
    </FullScreenPanel>
  );
}
